import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { authAPI } from '../services/api';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      loadDashboardStats();
    }
  }, [user]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getDashboardStats();
      setStats(response.data.stats);
    } catch (err) {
      setError('Failed to load dashboard statistics');
      console.error('Dashboard stats error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="dashboard">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1>Welcome back, {user.name}!</h1>
          <p>Here's what's happening with your account today.</p>
        </div>

        {user.role === 'customer' ? (
          <CustomerDashboard user={user} stats={stats} loading={loading} error={error} />
        ) : (
          <HelperDashboard user={user} stats={stats} loading={loading} error={error} />
        )}
      </div>
    </div>
  );
};

const CustomerDashboard = ({ user, stats, loading, error }) => {
  return (
    <div className="customer-dashboard">
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-cards">
          <Link to="/helpers" className="action-card">
            <div className="action-icon">🔍</div>
            <h3>Find Helpers</h3>
            <p>Browse and book trusted helpers in your area</p>
          </Link>
          
          <Link to="/my-bookings" className="action-card">
            <div className="action-icon">📅</div>
            <h3>My Bookings</h3>
            <p>View and manage your current bookings</p>
          </Link>
          
          <Link to="/my-subscriptions" className="action-card">
            <div className="action-icon">📋</div>
            <h3>My Subscriptions</h3>
            <p>Manage your monthly service subscriptions</p>
          </Link>
          
          <Link to="/chats" className="action-card">
            <div className="action-icon">💬</div>
            <h3>Open Chats</h3>
            <p>Message your helpers and customers</p>
          </Link>
          
          <Link to="/profile" className="action-card">
            <div className="action-icon">👤</div>
            <h3>Profile</h3>
            <p>Update your account information</p>
          </Link>
        </div>
      </div>

      <div className="dashboard-stats">
        <h2>Your Activity</h2>
        {loading ? (
          <div className="stats-loading">
            <div className="spinner"></div>
            <p>Loading statistics...</p>
          </div>
        ) : error ? (
          <div className="stats-error">
            <p>❌ {error}</p>
          </div>
        ) : (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats?.activeBookings || 0}</div>
              <div className="stat-label">Active Bookings</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats?.completedServices || 0}</div>
              <div className="stat-label">Completed Services</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats?.reviewsGiven || 0}</div>
              <div className="stat-label">Reviews Given</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const HelperDashboard = ({ user, stats, loading, error }) => {
  return (
    <div className="helper-dashboard">
      <div className="profile-status">
        <h2>Profile Status</h2>
        <div className="status-card">
          <div className="status-info">
            <h3>Your Profile</h3>
            <p>Rating: {stats?.averageRating || user.rating || 0}/5 ⭐</p>
            <p>Services: {user.services?.join(', ') || 'None set'}</p>
            <p>Hourly Rate: ₹{user.hourlyRate || 0}</p>
            <p>Total Reviews: {stats?.totalReviews || 0}</p>
          </div>
          <Link to="/profile" className="edit-profile-btn">
            Edit Profile
          </Link>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-cards">
          <Link to="/my-bookings" className="action-card">
            <div className="action-icon">💼</div>
            <h3>My Jobs</h3>
            <p>View pending and upcoming job requests</p>
          </Link>
          
          <Link to="/helper-subscriptions" className="action-card">
            <div className="action-icon">📋</div>
            <h3>Subscription Jobs</h3>
            <p>Manage monthly subscription assignments</p>
          </Link>
          
          <Link to="/chats" className="action-card">
            <div className="action-icon">💬</div>
            <h3>Open Chats</h3>
            <p>Message your customers</p>
          </Link>
          
          <Link to="/profile" className="action-card">
            <div className="action-icon">⚙️</div>
            <h3>Settings</h3>
            <p>Update availability and services</p>
          </Link>
        </div>
      </div>

      <div className="dashboard-stats">
        <h2>Your Performance</h2>
        {loading ? (
          <div className="stats-loading">
            <div className="spinner"></div>
            <p>Loading statistics...</p>
          </div>
        ) : error ? (
          <div className="stats-error">
            <p>❌ {error}</p>
          </div>
        ) : (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats?.pendingRequests || 0}</div>
              <div className="stat-label">Pending Requests</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats?.jobsCompleted || 0}</div>
              <div className="stat-label">Jobs Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats?.averageRating || 0}</div>
              <div className="stat-label">Average Rating</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard; 